import { useMemo, useState } from "preact/hooks";
import FullSite from "../desktop-pages/full-site";
import { Trans } from "@lingui/react/macro";
import { OutTotoEntry } from "../../../services/totocalcio/types";
import { TotoEvent, TotoTicket, useTotocalcioApi, useTotocalcioTournament, useTotocalcioTournamentsList } from "../../../services/totocalcio/totocalcio-service";

import { MyJackpotsView, SingleTournament, TotoTicketSidebar, TotoTournamentsList } from "../desktop-components/toto-jackpot";
import { Tab, TournamentFilter, TournamentPreviewWidget } from "../desktop-templates/toto-jackpot";
import { route } from "preact-router";
import User from "../../../services/user";

export interface TotoPageProps {
    tab?: 'all' | 'pending' | 'open' | 'completed' | 'my-bets';
    id?: string;
}

export const TABS = [
    { id: 'all', name: () => <Trans>All</Trans> },
    { id: 'open', name: () => <Trans>Open</Trans> },
    { id: 'pending', name: () => <Trans>Pending</Trans> },
    { id: 'completed', name: () => <Trans>Completed</Trans> },
] as const;

export enum JackpotViewType {
    GENERAL,
    SINGLE,
    MY_JACKPOTS,
}

export type PageState = { type: JackpotViewType.GENERAL | JackpotViewType.MY_JACKPOTS } | { type: JackpotViewType.SINGLE, tournament: OutTotoEntry };

export function TotoPage({ tab, id }: TotoPageProps) {
    const api = useTotocalcioApi();

    const currentTabId = tab || 'open';
    const mode = useMemo(() => {
        if (id) return { type: JackpotViewType.SINGLE, tournamentId: parseInt(id) };
        if (tab === 'my-bets') return { type: JackpotViewType.MY_JACKPOTS };
        return { type: JackpotViewType.GENERAL };
    }, [id, tab]);

    const tournaments = useTotocalcioTournamentsList(api, currentTabId == 'my-bets' ? 'open' : currentTabId);

    const currentTournament = useMemo(() => {
        if (mode.type !== JackpotViewType.SINGLE) return null;
        return tournaments.tournamentsList.find(t => t.tournament.id === mode.tournamentId);
    }, [tournaments.tournamentsList, mode]);

    const details = useTotocalcioTournament(api, currentTournament?.tournament);

    const ticketApi = useMemo(() => (
        details.tournamentInfo && currentTournament
            ? new TotoTicket(api, currentTournament.tournament, currentTournament.prizes, details.tournamentInfo)
            : null)
        , [api, currentTournament, details.tournamentInfo]
    );

    const handleOpenTournament = (tournament: OutTotoEntry) => {
        route(`/jackpots/${currentTabId}/${tournament.tournament.id}`);
    };

    const handleBackClick = () => {
        route(`/jackpots/${currentTabId === 'my-bets' ? 'open' : currentTabId}`);
    };

    const handleChangeTab = (tabId: string) => {
        route(`/jackpots/${tabId}`);
    };

    const ALL_TABS = [
        ...TABS,
        User.isLogged() ? { id: 'my-bets', name: () => <Trans>My History</Trans> } : null
    ].filter(x => !!x);

    return (
        <FullSite>
            <div class="flex flex-col min-h-screen bg-harif-950 text-white font-sans overflow-hidden border-t border-t-harif-950">
                <div class="flex flex-1 overflow-hidden">
                    <main class="flex-1 bg-harif-950">
                        {/* HEADER / FILTRI */}
                        <TournamentFilter>
                            {ALL_TABS.map(t => (
                                <Tab
                                    active={currentTabId === t.id}
                                    onClick={() => handleChangeTab(t.id)}
                                >
                                    {t.name()}
                                </Tab>
                            ))}
                        </TournamentFilter>

                        {/* BODY DINAMICO */}
                        {mode.type === JackpotViewType.GENERAL && !tournaments.isLoading && (
                            <TotoTournamentsList
                                handleOpenTournament={handleOpenTournament}
                                tournaments={tournaments}
                            />
                        )}

                        {mode.type === JackpotViewType.SINGLE && currentTournament && (
                            <SingleTournament
                                tournament={currentTournament}
                                handleBackClick={handleBackClick}
                                details={details}
                                ticketApi={ticketApi}
                            />
                        )}

                        {mode.type === JackpotViewType.MY_JACKPOTS && (
                            <MyJackpotsView api={api} />
                        )}

                    </main>

                    <aside class="w-80 p-2 bg-harif-900 border-l border-l-harif-950">
                        <TotoTicketSidebar ticketApi={ticketApi} isLogged={User.isLogged()} />
                    </aside>
                </div>
            </div>
        </FullSite>
    );
}